/**
 * Request Header Builder Utility
 *
 * Constructs HTTP headers for API test requests with gateway and external authentication.
 */

import { ExternalCredentials } from '../types';

/**
 * Build request headers with gateway authentication and external credentials
 *
 * @param apiKey - Gateway authentication API key
 * @param externalAuthType - Type of external authentication
 * @param externalCredentials - External API credentials
 * @returns Headers object ready for HTTP request
 *
 * @example
 * ```typescript
 * const headers = buildRequestHeaders(
 *   'mk_live_abc123',
 *   'Basic Auth',
 *   { username: 'ws.seclir', password: 'secret' }
 * );
 * // Returns:
 * // {
 * //   'Content-Type': 'application/json',
 * //   'X-API-Key': 'mk_live_abc123',
 * //   'X-External-Auth-Type': 'Basic Auth',
 * //   'X-External-Username': 'ws.seclir',
 * //   'X-External-Password': 'secret'
 * // }
 * ```
 */
export function buildRequestHeaders(
  apiKey: string,
  externalAuthType: string,
  externalCredentials: ExternalCredentials
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Key': apiKey,
    'X-External-Auth-Type': externalAuthType,
  };

  // Add external credential headers
  for (const field of Object.keys(externalCredentials)) {
    const headerName = `X-External-${capitalize(field)}`;
    headers[headerName] = externalCredentials[field];
  }

  return headers;
}

/**
 * Capitalize the first letter of a string
 *
 * @param str - String to capitalize
 * @returns Capitalized string
 */
function capitalize(str: string): string {
  if (!str) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}
